import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, Progress, Empty, Spin } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { statisticsService, InterceptReasonsStats, InterceptReason } from '../services/statistics';
import dayjs from 'dayjs';

interface InterceptReasonsTableProps {
  shortCode?: string;
  startDate?: string;
  endDate?: string;
}

const InterceptReasonsTable: React.FC<InterceptReasonsTableProps> = ({
  shortCode,
  startDate,
  endDate,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<InterceptReasonsStats | null>(null);

  useEffect(() => {
    if (shortCode) {
      loadData();
    }
  }, [shortCode, startDate, endDate]);

  const loadData = async () => {
    if (!shortCode) {
      return;
    }

    setLoading(true);
    try {
      const result = await statisticsService.getInterceptReasons({
        short_code: shortCode,
        start_date: startDate,
        end_date: endDate,
      });
      setData(result);
    } catch (error) {
      console.error('获取拦截原因统计失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 拦截类型标签颜色映射
  const getTypeColor = (type: string) => {
    const colorMap: Record<string, string> = {
      normal: 'success',
      audit: 'warning',
      blacklist: 'error',
      rule: 'processing',
      crawler: 'default',
    };
    return colorMap[type] || 'default';
  };

  // 拦截类型中文名称映射
  const getTypeName = (type: string) => {
    const nameMap: Record<string, string> = {
      normal: '正常',
      audit: '审核',
      blacklist: '黑名单',
      rule: '规则',
      crawler: '爬虫',
    };
    return nameMap[type] || type;
  };

  const columns = [
    {
      title: '拦截原因',
      dataIndex: 'reason',
      key: 'reason',
      width: 200,
      render: (text: string) => (
        <span style={{ fontWeight: 500 }}>{text}</span>
      ),
    },
    {
      title: '拦截类型',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type: string) => (
        <Tag color={getTypeColor(type)}>{getTypeName(type)}</Tag>
      ),
    },
    {
      title: '拦截次数',
      dataIndex: 'count',
      key: 'count',
      width: 120,
      align: 'right' as const,
      render: (count: number) => (
        <span style={{ fontSize: '14px', fontWeight: 500 }}>
          {count.toLocaleString()}
        </span>
      ),
    },
    {
      title: '占比',
      dataIndex: 'percentage',
      key: 'percentage',
      width: 200,
      render: (percentage: number, record: InterceptReason) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Progress
            percent={percentage}
            size="small"
            format={(percent) => `${percent?.toFixed(2)}%`}
            strokeColor={
              record.type === 'normal' ? '#52c41a' :
              record.type === 'audit' ? '#faad14' :
              record.type === 'blacklist' ? '#ff4d4f' :
              '#1890ff'
            }
          />
        </div>
      ),
    },
  ];

  if (!shortCode) {
    return (
      <Card title={<><WarningOutlined /> 拦截原因统计</>}>
        <Empty
          description="请在上方输入短链码并点击搜索按钮，查看该链接的拦截原因统计"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  return (
    <Card
      title={<><WarningOutlined /> 拦截原因统计</>}
      extra={
        data && (
          <span style={{ fontSize: '14px', color: '#666' }}>
            总访问: <strong>{data.total_visits.toLocaleString()}</strong> | 
            正常: <strong style={{ color: '#52c41a' }}>{data.normal_visits.toLocaleString()}</strong> | 
            拦截: <strong style={{ color: '#ff4d4f' }}>{data.intercepted_visits.toLocaleString()}</strong>
          </span>
        )
      }
    >
      <Spin spinning={loading}>
        {data ? (
          <Table
            columns={columns}
            dataSource={data.intercept_reasons}
            rowKey={(record) => `${record.reason}-${record.type}`}
            pagination={false}
            size="middle"
          />
        ) : (
          <Empty description="暂无数据" />
        )}
      </Spin>
    </Card>
  );
};

export default InterceptReasonsTable;
